/**
 * projectLinkListResource — which Forge project is which CMMD project.
 *
 * Every surface that draws a list of projects needs this to show a project's
 * real identity, and the per-project link read cannot serve them: one request
 * per row is a request storm, and the global breadcrumb in particular must
 * never be the reason a request happens. So the mapping is fetched once and
 * read synchronously, in the `subscribe` / `read` / hook shape the other CMMD
 * resources use.
 *
 * The mapping ONLY. A project's icon is CMMD's record and is read from the
 * work-intake projects snapshot; nothing here caches an icon, because a second
 * copy of an identity CMMD owns is how the two products end up disagreeing
 * about what a project looks like.
 */

import { useSyncExternalStore } from "react";

import { cmmdApiClient } from "~/environments/cmmdApi";

/** One row of the mapping, reduced to what resolving an identity needs. */
export interface ProjectLinkRow {
  readonly forgeEnvironmentId: string;
  readonly forgeProjectId: string;
  readonly cmmdProjectId: number;
}

export interface ProjectLinkListSnapshot {
  readonly links: readonly ProjectLinkRow[];
  readonly loaded: boolean;
}

type Listener = () => void;

const EMPTY_SNAPSHOT: ProjectLinkListSnapshot = { links: [], loaded: false };

let snapshot: ProjectLinkListSnapshot = EMPTY_SNAPSHOT;
let inFlight: Promise<void> | null = null;
let loadGeneration = 0;
let reloadAfterInFlight = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelayMs = 1_000;
const listeners = new Set<Listener>();
const MAX_RETRY_DELAY_MS = 30_000;

function shouldRetryProjectLinkListError(error: unknown): boolean {
  if (!error || typeof error !== "object") return true;
  const status = (error as { readonly status?: unknown }).status;
  if (typeof status !== "number") return true;
  return status === 408 || status === 429 || status >= 500;
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setSnapshot(next: ProjectLinkListSnapshot): void {
  snapshot = next;
  emit();
}

function clearRetryTimer(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

export async function fetchProjectLinkListSnapshot(): Promise<ProjectLinkListSnapshot> {
  const response = await cmmdApiClient.listProjectLinks();
  return {
    links: response.links.map((link) => ({
      forgeEnvironmentId: link.forgeEnvironmentId,
      forgeProjectId: link.forgeProjectId,
      cmmdProjectId: link.cmmdProjectId,
    })),
    loaded: response.sources?.cmmdAvailable !== false,
  };
}

function scheduleRetry(): void {
  if (retryTimer !== null || listeners.size === 0) return;
  const delayMs = retryDelayMs;
  retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_DELAY_MS);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (listeners.size === 0 || snapshot.loaded) return;
    ensureLoaded();
  }, delayMs);
}

/**
 * Load once, and never let a failure become a permanent empty answer.
 *
 * On failure the snapshot stays unloaded rather than being marked loaded-and-
 * empty: "no links" and "we could not ask" render identically (a favicon), but
 * only one of them should be retried, and marking a failure as loaded would
 * pin every linked project to its checkout favicon for the rest of the session.
 */
function ensureLoaded(): void {
  if (snapshot.loaded) return;
  if (inFlight !== null) return;
  const generation = loadGeneration;
  // `subscribe` runs inside React's commit, so a client that throws
  // synchronously — an older bridge without this route, a torn-down transport —
  // would take the whole tree down for the sake of a 14px glyph. Start the
  // request inside a promise so a synchronous throw lands in the same place a
  // rejection does.
  inFlight = Promise.resolve()
    .then(() => fetchProjectLinkListSnapshot())
    .then((next) => {
      if (generation !== loadGeneration) return;
      setSnapshot(next);
      if (next.loaded) {
        retryDelayMs = 1_000;
      } else {
        scheduleRetry();
      }
    })
    .catch((error: unknown) => {
      if (generation !== loadGeneration) return;
      // Leave the snapshot unloaded so a later subscriber retries.
      if (!snapshot.loaded && shouldRetryProjectLinkListError(error)) scheduleRetry();
    })
    .finally(() => {
      inFlight = null;
      if (reloadAfterInFlight && listeners.size > 0 && !snapshot.loaded) {
        reloadAfterInFlight = false;
        ensureLoaded();
      }
    });
}

/**
 * Drop the cached mapping so the next reader refetches.
 *
 * Linking or unlinking a project changes this table, and a stale mapping shows
 * the wrong project's icon — which is worse than showing none.
 */
export function invalidateProjectLinkList(): void {
  clearRetryTimer();
  retryDelayMs = 1_000;
  loadGeneration += 1;
  setSnapshot(EMPTY_SNAPSHOT);
  if (inFlight !== null) {
    reloadAfterInFlight = true;
    return;
  }
  if (listeners.size > 0) ensureLoaded();
}

/** Exported for the hook and for tests, which drive it the way React does. */
export function subscribeProjectLinkList(listener: Listener): () => void {
  listeners.add(listener);
  ensureLoaded();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) clearRetryTimer();
  };
}

export function readProjectLinkListSnapshot(): ProjectLinkListSnapshot {
  return snapshot;
}

export function useProjectLinkListSnapshot(): ProjectLinkListSnapshot {
  return useSyncExternalStore(
    subscribeProjectLinkList,
    readProjectLinkListSnapshot,
    readProjectLinkListSnapshot,
  );
}

/** Test seam: the module-level cache would otherwise leak between tests. */
export function resetProjectLinkListForTests(): void {
  clearRetryTimer();
  retryDelayMs = 1_000;
  loadGeneration = 0;
  reloadAfterInFlight = false;
  snapshot = EMPTY_SNAPSHOT;
  inFlight = null;
  listeners.clear();
}
